import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Award, Mail, Users, TrendingUp, TrendingDown,
  ArrowRight, Globe, FolderOpen, Clock, CheckCircle,
  AlertCircle, Zap, BarChart2, Eye,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/* ── Stat card ── */
function StatCard({ label, value, change, up, icon, accent, delay = 0 }: {
  label: string; value: string; change: string; up: boolean;
  icon: React.ReactNode; accent: string; delay?: number;
}) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '24px', position: 'relative', overflow: 'hidden',
      transition: 'border-color 0.2s, transform 0.2s',
      animation: `slideUp 0.4s ${delay}ms var(--ease) both`,
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '44'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: -20, right: -20,
        width: 100, height: 100, borderRadius: '50%',
        background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: accent + '18', border: `1px solid ${accent}30`, flexShrink: 0,
        }}>
          <span style={{ color: accent }}>{icon}</span>
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500,
          color: up ? 'var(--green)' : 'var(--red)',
          background: up ? 'var(--green-d)' : 'var(--red-d)',
          padding: '3px 8px', borderRadius: 100,
        }}>
          {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {change}
        </span>
      </div>

      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 4, letterSpacing: '-0.5px' }}>
        {value}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text3)', fontWeight: 400 }}>{label}</div>
    </div>
  );
}

/* ── Quick action ── */
function QuickAction({ icon, label, desc, to, delay = 0 }: {
  icon: React.ReactNode; label: string; desc: string; to: string; delay?: number;
}) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '16px 18px', cursor: 'pointer', textAlign: 'left',
      transition: 'all 0.18s', animation: `slideUp 0.4s ${delay}ms var(--ease) both`,
      fontFamily: "'Inter',sans-serif",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(191,61,12,0.3)'; e.currentTarget.style.background = 'rgba(191,61,12,0.06)'; e.currentTarget.style.transform = 'translateX(3px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 9, flexShrink: 0,
        background: 'rgba(191,61,12,0.12)', border: '1px solid rgba(191,61,12,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--or)',
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 1 }}>{desc}</div>
      </div>
      <ArrowRight size={14} color="var(--text3)" />
    </button>
  );
}

/* ── Activity item ── */
function ActivityItem({ icon, text, time, type }: {
  icon: React.ReactNode; text: string; time: string; type: 'success' | 'info' | 'warning';
}) {
  const colors = { success: 'var(--green)', info: 'var(--blue)', warning: 'var(--yellow)' };
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: colors[type] + '18', display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: colors[type],
        border: `1px solid ${colors[type]}30`,
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--text)', lineHeight: 1.4 }}>{text}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>{time}</div>
      </div>
    </div>
  );
}

/* ── Status badge ── */
function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      {ok
        ? <CheckCircle size={14} color="var(--green)" style={{ flexShrink: 0 }} />
        : <AlertCircle size={14} color="var(--red)" style={{ flexShrink: 0 }} />}
      <span style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 300, flex: 1 }}>{label}</span>
      <span style={{
        fontSize: 11, fontWeight: 600, color: ok ? 'var(--green)' : 'var(--red)',
        background: ok ? 'var(--green-d)' : 'var(--red-d)',
        padding: '2px 8px', borderRadius: 100,
      }}>{ok ? 'ONLINE' : 'ISSUE'}</span>
    </div>
  );
}

/* ─────────────────── MAIN PAGE ─────────────────── */
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 60000); return () => clearInterval(t); }, []);

  const greet = () => {
    const h = time.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <>
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or: #BF3D0C; --or2: #E8593C; --or-dim: rgba(191,61,12,0.10);
        //   --text: #e8e6de; --text2: #b4b2a9; --text3: #6a6a65;
        //   --border: rgba(255,255,255,0.08);
        //   --card: rgba(255,255,255,0.025);
        //   --row-hover: rgba(255,255,255,0.025);
        //   --surface: #111110;
        //   --header-bg: #0e0d0c;
        // }
        body { margin: 0; font-family: 'DM Sans', sans-serif; }
        input::placeholder { color: var(--text3); }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes toastIn     { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        @keyframes modalIn     { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
        .ap-row { transition: background 0.12s; }
        .ap-row:hover { background: var(--row-hover) !important; }
        .ap-th { user-select: none; }
        .ap-th:hover { color: var(--text) !important; }
        .ap-page-btn { transition: all 0.15s; }
        .ap-page-btn:hover:not(:disabled) { background: var(--or-dim) !important; color: var(--or) !important; border-color: rgba(191,61,12,0.3) !important; }
        .ap-filter-btn { transition: all 0.15s; }
        .ap-filter-btn:hover { background: rgba(255,255,255,0.05) !important; }
      `}</style>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px', animation: 'fadeSlideIn 0.4s ease both' }}>

        {/* ── Page header ── */}
        <div style={{ marginBottom: 32, animation: 'fadeIn 0.4s ease both' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.4px' }}>
                {greet()}, {user?.name?.split(' ')[0] || 'Admin'} 👋
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text3)', fontWeight: 300 }}>
                {time.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {' · '}Here's what's happening with your website.
              </p>
            </div>

            <button onClick={() => navigate('/content/products')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--or)', border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 14, fontWeight: 500, padding: '10px 20px',
              cursor: 'pointer', fontFamily: "'Inter',sans-serif",
              boxShadow: '0 4px 14px rgba(191,61,12,0.3)', transition: 'all 0.18s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--or2)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--or)'; e.currentTarget.style.transform = 'none'; } }
            >
              <Zap size={14} /> Quick Edit
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 32 }} className="stats-grid">
          <StatCard label="Content Sections" value="12" change="+2 new" up icon={<FileText size={16} />} accent="#3b82f6" delay={0} />
          <StatCard label="Certifications" value="6" change="All valid" up icon={<Award size={16} />} accent="#22c55e" delay={60} />
          <StatCard label="Uploaded Files" value="0" change="Upload now" up={false} icon={<FolderOpen size={16} />} accent="#f59e0b" delay={120} />
          <StatCard label="Pages Live" value="7" change="All online" up icon={<Globe size={16} />} accent="#bf3d0c" delay={180} />
        </div>

        {/* ── Main grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }} className="dash-grid">

          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Quick actions */}
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>Quick Actions</h2>
                  <p style={{ fontSize: 13, color: 'var(--text3)' }}>Jump to the most common tasks</p>
                </div>
                <Zap size={16} color="var(--or)" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <QuickAction icon={<Globe size={16} />} label="Edit Home Page" desc="Update hero, stats & CTA content" to="/content/home" delay={0} />
                <QuickAction icon={<Award size={16} />} label="Manage Certifications" desc="Update cert cards & upload documents" to="/content/certifications" delay={60} />
                <QuickAction icon={<FolderOpen size={16} />} label="Upload Files" desc="Add PDFs & documents to file manager" to="/files" delay={120} />
                <QuickAction icon={<Mail size={16} />} label="Edit Contact Page" desc="Update FAQs, offices & contact info" to="/content/contact" delay={180} />
                <QuickAction icon={<Users size={16} />} label="Edit About Page" desc="Manage team, story & values section" to="/content/about" delay={240} />
              </div>
            </div>

            {/* Page overview */}
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>Website Pages</h2>
                  <p style={{ fontSize: 13, color: 'var(--text3)' }}>All live pages and their status</p>
                </div>
                <BarChart2 size={16} color="var(--text3)" />
              </div>
              <div>
                {[
                  { page: 'Home', path: '/', sections: 5, status: 'live' },
                  { page: 'Products', path: '/products', sections: 5, status: 'live' },
                  { page: 'Industries', path: '/industries', sections: 6, status: 'live' },
                  { page: 'Certifications', path: '/certifications', sections: 4, status: 'live' },
                  { page: 'About', path: '/about', sections: 5, status: 'live' },
                  { page: 'Contact', path: '/contact', sections: 5, status: 'live' },
                ].map((p, i) => (
                  <div key={p.page} style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0',
                    borderBottom: i < 5 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: 'var(--card2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 600, color: 'var(--text2)', flexShrink: 0,
                    }}>{p.page[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{p.page}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{p.sections} editable sections</div>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: 'var(--green)',
                      background: 'var(--green-d)', padding: '2px 8px', borderRadius: 100,
                    }}>{p.status.toUpperCase()}</span>
                    <a href={`http://localhost:3001${p.path}`} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--text3)', transition: 'color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                    >
                      <Eye size={14} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Recent activity */}
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>Recent Activity</h2>
                  <p style={{ fontSize: 13, color: 'var(--text3)' }}>Latest changes & events</p>
                </div>
                <Clock size={15} color="var(--text3)" />
              </div>
              <ActivityItem icon={<CheckCircle size={14} />} text="Admin dashboard deployed successfully" time="Just now" type="success" />
              <ActivityItem icon={<Globe size={14} />} text="Home page content schema ready" time="2 min ago" type="info" />
              <ActivityItem icon={<Award size={14} />} text="6 certification cards configured" time="5 min ago" type="success" />
              <ActivityItem icon={<AlertCircle size={14} />} text="No PDF files uploaded yet" time="Pending" type="warning" />
              <ActivityItem icon={<Users size={14} />} text="Contact page FAQs configured" time="10 min ago" type="success" />
              <div style={{ textAlign: 'center', paddingTop: 16 }}>
                <button style={{ fontSize: 13, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>
                  View all activity →
                </button>
              </div>
            </div>

            {/* System status */}
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>System Status</h2>
                  <p style={{ fontSize: 13, color: 'var(--text3)' }}>All services health check</p>
                </div>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', animation: 'pulse-ring 2s ease infinite' }} />
              </div>
              <StatusBadge ok label="Admin Dashboard" />
              <StatusBadge ok label="Content API" />
              <StatusBadge ok label="File Storage" />
              <StatusBadge ok label="Main Website" />
              <StatusBadge ok={false} label="PDF Uploads" />
              <div style={{ marginTop: 16, padding: '12px', background: 'var(--card2)', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>UPTIME</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)', letterSpacing: '-0.5px' }}>99.9%</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Last 30 days</div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
        @media (max-width: 1200px) { .dash-grid  { grid-template-columns: 1fr !important; } }
        @media (max-width: 900px)  { .stats-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 500px)  { .stats-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 768px)  { main { padding: 20px 16px !important; } }
      `}</style>
      </div></>
  );
}
